import React, { useContext, useState } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2'
import Icon from '@embracesbs/component-icon'
import Modal from '@embracesbs/component-modal'
import { DeployStatus } from '../../Interfaces/Types'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import { useTenants } from '../Context/TenantContext'
import { useApi } from '../../Hooks'

// "Advanced" tab: destructive / admin-only tenant actions (the Danger Zone).
const TenantAdvanced: React.FC = () => {
  const roles = useContext(AuthRoleContext)
  const { selectedTenant, setSelectedTenantId, setTenantDeployStatus } = useTenants()
  const { fetchApi } = useApi()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isClicked, setIsClicked] = useState<boolean>(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const isUpdating = (state?: DeployStatus) => state === 'updating'

  // The API returns the refusal as a plain-text body (e.g. the freshness check on a
  // stale tenant), so surface that rather than a generic failure.
  const apiMessage = (error: any): string =>
    typeof error?.data === 'string' && error.data.length > 0 ? error.data : (error?.message ?? 'Something went wrong.')

  const lastDeployLabel = selectedTenant?.lastSuccessfulDeployUtc
    ? new Date(selectedTenant.lastSuccessfulDeployUtc).toLocaleString()
    : 'never recorded'

  // Greyed out while an update runs, while the delete request is in flight,
  // and once the tenant is already awaiting its mail or being torn down.
  const deleteInProgress = selectedTenant?.deployStatus === 'pendingDelete' || selectedTenant?.deployStatus === 'deleting'
  const isDeleteBlocked = isUpdating(selectedTenant?.deployStatus) || isClicked || deleteInProgress

  const onDeleteConfirm = async () => {
    setIsClicked(true)
    setDeleteError(null)
    const tenantId = selectedTenant?.id
    try {
      await fetchApi({
        endpoint: `/api/tenant/delete/pending/${tenantId}`,
        method: 'PUT'
      })
      // Show the pending-delete state in the list right away.
      if (tenantId) setTenantDeployStatus(tenantId, 'pendingDelete')
      setSelectedTenantId(null)
    } catch (error: any) {
      // E.g. the API refused because the confirmation mail could not be sent —
      // the message renders in the modal's error banner. Only re-enable here:
      // on success this component unmounts with the tenant deselected.
      setDeleteError(apiMessage(error))
      setIsClicked(false)
      console.error(error)
    }
  }

  if (!roles?.isAdmin) {
    return (
      <Text align='center' textColor='grey'>
        Advanced tenant actions are only available to administrators
      </Text>
    )
  }

  return (
    <>
      <div
        style={{
          border: '1px solid var(--tma-danger-border)',
          background: 'var(--tma-danger-tint)',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '8px',
          marginBottom: '16px'
        }}
      >
        <Text textStyle='h6' content='Danger Zone' style={{ color: 'var(--tma-danger-text)' }} />
        <div style={{ margin: '8px 0 12px' }}>
          <Text
            textStyle='description'
            textColor='grey'
            content='Permanently delete this tenant and all of its resources. This cannot be undone.'
          />
        </div>
        <div style={{ margin: '0 0 12px' }}>
          <Text
            textStyle='description'
            textColor='grey'
            content={`A tenant must have deployed recently before it can be destroyed, so the teardown runs against current modules. Last successful deploy: ${lastDeployLabel}.`}
          />
        </div>
        <Button2
          buttonRadius='8px'
          buttonSize='40px'
          text={deleteInProgress ? 'Deletion pending' : 'Delete Tenant'}
          isDanger={!isDeleteBlocked}
          leftElement={<Icon iconName='Delete1' size={16} />}
          onClick={() => setIsModalOpen(true)}
          isDisabled={isDeleteBlocked}
          variant='contained'
        />
      </div>
      <Modal
        isOpen={isModalOpen}
        width='45%'
        onClose={() => setIsModalOpen(false)}
        onCancel={() => setIsModalOpen(false)}
        hasHeaderBorder={true}
        headerTitle={`Deleting tenant '${selectedTenant?.tenantDisplayname}'`}
        height={'auto'}
        isConfirmDisabled={isClicked}
        onConfirm={onDeleteConfirm}
        variant='confirm'
        labelConfirm={isClicked ? 'Sending…' : 'Delete'}
        labelCancel='Cancel'
      >
        <Text>
          {`You are about to delete ${selectedTenant?.tenantDisplayname}. `}
          <span style={{ color: 'var(--tma-danger-text)' }}>All resources, including data, will be permanently removed</span> for this
          tenant.
        </Text>
        <br />
        <Text>A confirmation email will be sent to you with a link to complete the deletion process.</Text>
        {deleteError && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--tma-danger-border)',
              background: 'var(--tma-danger-tint)'
            }}
          >
            <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={deleteError} />
          </div>
        )}
      </Modal>
    </>
  )
}

export default TenantAdvanced
